import { Box, CircularProgress, Divider, LinearProgress, List, ListItem } from '@mui/material';
import { forwardRef, useCallback, useEffect, useMemo } from 'react';
import { Components, Virtuoso } from 'react-virtuoso';
import useMockLoading from '../../../../../../../../../components/Hooks/useMockLoading';
import { useChatContext } from '../../../../../../../contexts/chat/chat.context';
import { ChatState } from '../../../../../../../contexts/chat/hooks/useChats/reducer/chatListReducer.types';
import { CHAT_FOOTER_HEIGHT } from '../../../utils/styles.const';
import ChatRoomItem from '../common/ChatRoomItem/ChatRoomItem';
import CreateNewChatFooter from '../common/CreateNewChatFooter/CreateNewChatFooter';
import { useChatRoomsContentStyles } from './chatRoomsContent.styles';

const CustomList: Components<ChatState>['List'] = forwardRef(
  ({ children, style, ...props }, listRef) => (
    <List
      ref={listRef}
      component="div"
      {...props}
      style={{ ...style, margin: 0, overflow: 'hidden' }}
    >
      {children}
    </List>
  ),
);

const CustomItem: Components<ChatState>['Item'] = ({ children, style, ...props }) => (
  <>
    <ListItem component="div" {...props} style={{ ...style, margin: 0, padding: 0 }}>
      {children}
    </ListItem>
    <Divider />
  </>
);

const ChatRoomsContent = () => {
  const { classes } = useChatRoomsContentStyles();

  const { selectedGroupOfRooms, fetchChatsFromGroup, rooms } = useChatContext();

  const { handleAsyncFunction: handleFetchChatsFromGroup, isLoading } = useMockLoading({
    asyncFunction: fetchChatsFromGroup,
  });

  useEffect(() => {
    if (selectedGroupOfRooms && selectedGroupOfRooms.chatIdsPresentInThisGrouping.length) {
      handleFetchChatsFromGroup(selectedGroupOfRooms.metaId);
      return;
    }
  }, [selectedGroupOfRooms?.metaId]);

  const endReached = useCallback(() => {
    if (!selectedGroupOfRooms?.metaId) {
      return;
    }
    handleFetchChatsFromGroup(selectedGroupOfRooms.metaId);
  }, [fetchChatsFromGroup, selectedGroupOfRooms]);

  const chatsAndMainChat = useMemo(() => {
    if (!selectedGroupOfRooms) {
      return [];
    }
    return selectedGroupOfRooms.mainChat
      ? [selectedGroupOfRooms.mainChat, ...selectedGroupOfRooms.chats]
      : selectedGroupOfRooms.chats;
  }, [selectedGroupOfRooms]);

  const showCircularProgress =
    selectedGroupOfRooms && !selectedGroupOfRooms.chats.length && !selectedGroupOfRooms.mainChat;
  const showLinearProgress = !showCircularProgress && isLoading;
  const showCreateChatButton =
    (selectedGroupOfRooms && !selectedGroupOfRooms.mainChat) || rooms.length === 1;

  return (
    <Box
      sx={{
        height: '100%',
      }}
    >
      {showCircularProgress && (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '70%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={20} />
        </Box>
      )}
      <Box className={classes.list}>
        <Virtuoso
          data={chatsAndMainChat}
          endReached={endReached}
          increaseViewportBy={{ bottom: 100, top: 100 }}
          itemContent={(_, room) => <ChatRoomItem chat={room} />}
          components={{
            List: CustomList,
            Item: CustomItem,
          }}
        />
      </Box>
      {showLinearProgress && <LinearProgress />}
      {showCreateChatButton && (
        <>
          <Divider />

          <Box
            sx={{
              height: CHAT_FOOTER_HEIGHT,
            }}
          >
            <CreateNewChatFooter />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ChatRoomsContent;
